import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Alert,
} from "@mui/material";
import Logger from "../../utils/logger";
import { fetchRubricCatalog } from "../../services/api";
import {
  DIFFICULTIES,
  DIFFICULTY_LABELS,
  RubricAxisData,
  RubricCatalog,
  RubricEvaluationVersion,
  RubricSectionData,
} from "../../types";

// 画面で切り替えられる採点ルーブリックのバージョン（全パターン明示列挙）。
// 既定は本番に配信済みの版にする（未デプロイの版を既定にすると初回表示が失敗するため）。
const RUBRIC_EVALUATION_VERSIONS: RubricEvaluationVersion[] = ["3.0.0"];
// 既定は現行の配信版。旧版は過去の採点ログを読むときのために選べるようにしておく
const DEFAULT_RUBRIC_EVALUATION_VERSION: RubricEvaluationVersion = "3.0.0";

// 得点を「+3」「0」のように表示する（加点なしは符号なし）
const formatPoint = (point: number): string => (point > 0 ? `+${point}` : `${point}`);

// チェックリスト方式のセクションを表（項目 × 4段階）で表示する
const ChecklistSectionTable: React.FC<{ section: RubricSectionData }> = ({ section }) => (
  <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto" }}>
    <Table size="small" sx={{ minWidth: 720 }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 600, minWidth: 180 }}>項目</TableCell>
          <TableCell sx={{ fontWeight: 600, minWidth: 130 }}>十分</TableCell>
          <TableCell sx={{ fontWeight: 600, minWidth: 130 }}>おおむね</TableCell>
          <TableCell sx={{ fontWeight: 600, minWidth: 130 }}>一部</TableCell>
          <TableCell sx={{ fontWeight: 600, minWidth: 130 }}>不十分</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {(section.checklistItems ?? []).map((checklistItem) => (
          <TableRow key={checklistItem.item}>
            <TableCell sx={{ verticalAlign: "top" }}>
              <Typography variant="body2" fontWeight="bold">
                {checklistItem.item}
              </Typography>
            </TableCell>
            {[
              checklistItem.full,
              checklistItem.most,
              checklistItem.partial,
              checklistItem.none,
            ].map((level, levelIndex) => (
              <TableCell key={levelIndex} sx={{ verticalAlign: "top" }}>
                {level ? (
                  <>
                    <Chip
                      label={formatPoint(level.point)}
                      size="small"
                      color={level.point > 0 ? "primary" : "default"}
                      sx={{ mb: 0.5 }}
                    />
                    <Typography variant="caption" color="text.secondary" display="block">
                      {level.description}
                    </Typography>
                  </>
                ) : (
                  // その項目が持たない判定（段階の数は項目ごとに2〜4）
                  <Typography variant="caption" color="text.disabled">
                    なし
                  </Typography>
                )}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

// バンド方式のセクションを表（得点帯 × 基準）で表示する
const BandSectionTable: React.FC<{ section: RubricSectionData }> = ({ section }) => (
  <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto" }}>
    <Table size="small" sx={{ minWidth: 480 }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>得点帯</TableCell>
          <TableCell sx={{ fontWeight: 600 }}>基準</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {(section.bands ?? []).map((band) => (
          <TableRow key={`${band.min}-${band.max}`}>
            <TableCell sx={{ verticalAlign: "top", whiteSpace: "nowrap" }}>
              <Chip label={`${band.min}〜${band.max}点`} size="small" variant="outlined" />
            </TableCell>
            <TableCell sx={{ verticalAlign: "top" }}>
              <Typography variant="body2">{band.description}</Typography>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

// 1つの採点軸（内容など）配下の全セクションを表示する
const AxisSection: React.FC<{ axis: RubricAxisData }> = ({ axis }) => (
  <Card variant="outlined" sx={{ mb: 3 }}>
    <CardContent>
      <Typography variant="h6" gutterBottom>
        {axis.axisLabel}
        <Chip label={`${axis.maxPoints}点満点`} size="small" sx={{ ml: 1 }} />
      </Typography>
      {axis.sections.map((section) => (
        <Box key={section.label} sx={{ mt: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {section.label}
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              {section.maxPoints}点
            </Typography>
          </Typography>
          {section.method === "checklist" ? (
            <ChecklistSectionTable section={section} />
          ) : (
            <BandSectionTable section={section} />
          )}
        </Box>
      ))}
    </CardContent>
  </Card>
);

const RubricList: React.FC = () => {
  const [selectedEvaluationVersion, setSelectedEvaluationVersion] =
    useState<RubricEvaluationVersion>(DEFAULT_RUBRIC_EVALUATION_VERSION);
  const [rubricCatalog, setRubricCatalog] = useState<RubricCatalog | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0);
  const [selectedDifficulty, setSelectedDifficulty] = useState<number>(1);

  useEffect(() => {
    const loadRubricCatalog = async () => {
      setLoading(true);
      setError(null);
      try {
        const catalog = await fetchRubricCatalog(selectedEvaluationVersion);
        setRubricCatalog(catalog);
      } catch (fetchError) {
        Logger.error("Failed to fetch rubric catalog:", fetchError);
        setRubricCatalog(null);
        setError(
          `採点ルーブリック（v${selectedEvaluationVersion}）の取得に失敗しました。` +
            "この版がまだデプロイされていない可能性があります"
        );
      } finally {
        setLoading(false);
      }
    };
    loadRubricCatalog();
  }, [selectedEvaluationVersion]);

  const selectedCategory = rubricCatalog?.categories[selectedCategoryIndex];
  // v3.0.0 以降は全員共通のルーブリックで難易度の階層を持たない。
  // 旧バージョン（byDifficulty あり）は選択中の難易度の分を引く。
  const hasDifficultyLevels = Boolean(selectedCategory?.byDifficulty);
  const selectedAxes = selectedCategory?.byDifficulty
    ? selectedCategory.byDifficulty.find(
        (difficultyData) => difficultyData.difficulty === selectedDifficulty
      )?.axes
    : selectedCategory?.axes;

  const handleDifficultyChange = (
    _event: React.MouseEvent<HTMLElement>,
    nextDifficulty: number | null
  ) => {
    if (nextDifficulty !== null) {
      setSelectedDifficulty(nextDifficulty);
    }
  };

  // 版によってカテゴリの並びが変わりうるため、切り替え時はカテゴリ選択を先頭へ戻す
  const handleEvaluationVersionChange = (
    _event: React.MouseEvent<HTMLElement>,
    nextEvaluationVersion: RubricEvaluationVersion | null
  ) => {
    if (nextEvaluationVersion !== null) {
      setSelectedCategoryIndex(0);
      setSelectedEvaluationVersion(nextEvaluationVersion);
    }
  };

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            採点ルーブリック
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            実際に採点に使われているルーブリックです。評価バージョンとカテゴリで絞り込めます。
            難易度で採点基準が変わるバージョンでは、難易度の切り替えも出ます。
          </Typography>

          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              評価バージョン
            </Typography>
            <ToggleButtonGroup
              value={selectedEvaluationVersion}
              exclusive
              onChange={handleEvaluationVersionChange}
              size="small"
            >
              {RUBRIC_EVALUATION_VERSIONS.map((evaluationVersion) => (
                <ToggleButton key={evaluationVersion} value={evaluationVersion}>
                  v{evaluationVersion}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {rubricCatalog && (
            <Chip
              label={`プロンプトバージョン: ${rubricCatalog.promptVersion}`}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ mt: 2 }}
            />
          )}
        </CardContent>
      </Card>

      {loading && (
        <Box display="flex" justifyContent="center" mt={6}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && rubricCatalog && (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Tabs
                value={selectedCategoryIndex}
                onChange={(_event, nextIndex: number) => setSelectedCategoryIndex(nextIndex)}
                variant="scrollable"
                scrollButtons="auto"
              >
                {rubricCatalog.categories.map((category) => (
                  <Tab key={category.category} label={category.categoryLabel} />
                ))}
              </Tabs>

              {hasDifficultyLevels && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    難易度
                  </Typography>
                  <ToggleButtonGroup
                    value={selectedDifficulty}
                    exclusive
                    onChange={handleDifficultyChange}
                    size="small"
                  >
                    {DIFFICULTIES.map((difficulty) => (
                      <ToggleButton key={difficulty} value={difficulty}>
                        {DIFFICULTY_LABELS[difficulty]}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>
              )}
            </CardContent>
          </Card>

          {selectedAxes ? (
            selectedAxes.map((axis) => <AxisSection key={axis.axis} axis={axis} />)
          ) : (
            <Alert severity="info">このカテゴリのルーブリックが見つかりませんでした</Alert>
          )}
        </>
      )}
    </Box>
  );
};

export default RubricList;
